import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Card, Row, Col, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import ReactCountryFlag from 'react-country-flag';
import convertCountryCodeToAlpha2 from '../../utils/countryCodeConverter';
import { getPlayer } from '../../services/api';
import { Player } from '../../models/Player';

const PlayerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [player, setPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  // Check if we came from a tournament page
  const fromTournament = location.state?.fromTournament;
  const tournamentId = location.state?.tournamentId;
  const tournamentName = location.state?.tournamentName;

  useEffect(() => {
    const fetchPlayer = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const data = await getPlayer(id);
        setPlayer(data);
      } catch (err) {
        console.error('Error fetching player details:', err);
        setError('Failed to load player details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlayer();
  }, [id]);

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!player) {
    return <Alert variant="warning">Player not found</Alert>;
  }

  // Sort ratings by year and week (most recent first)
  const sortedRatings = [...player.ratings].sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.week - a.week;
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <div className="d-flex align-items-center mb-1">
            {player.nationality && (
              <span className="me-2" title={player.nationality}>
                <ReactCountryFlag
                  countryCode={convertCountryCodeToAlpha2(player.nationality)}
                  svg
                  style={{ width: '1.5em', height: '1.5em', paddingTop: '2px' }}
                />
              </span>
            )}
            <h2 className="mb-0 me-2">{player.name}</h2>
            {player.ageRange && <Badge bg="secondary" className="py-1 px-2 align-self-center me-2">{player.ageRange}</Badge>}
            {player.isPro && <Badge bg="success" className="py-1 px-2 align-self-center me-1">Pro</Badge>}
            {!player.isPro && <Badge bg="primary" className="py-1 px-2 align-self-center me-1">Amateur</Badge>}
          </div>
        </div>
        <div>
          {fromTournament && tournamentId ? (
            <Link
              to={`/tournaments/${tournamentId}`}
              className="btn btn-outline-secondary me-2"
            >
              <i className="bi bi-arrow-left me-1"></i>
              Back to {tournamentName || 'Tournament'}
            </Link>
          ) : null}
          <Link to="/players" className="btn btn-outline-primary">Back to Search</Link>
        </div>
      </div>

      <Row>
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>
              <h4>Player Information</h4>
            </Card.Header>
            <Card.Body className="p-3">
              <div className="d-flex flex-wrap">
                {/* Row 1: Nationality | Location */}
                <div className="col-12 col-sm-6 mb-2 pe-sm-2">
                  <div className="d-flex">
                    <div className="text-muted me-2">Nationality:</div>
                    <div>{player.nationality || 'Not specified'}</div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 mb-2">
                  <div className="d-flex">
                    <div className="text-muted me-2">Location:</div>
                    <div>{player.location || 'Not specified'}</div>
                  </div>
                </div>

                {/* Row 2: Gender | Age Range */}
                <div className="col-12 col-sm-6 mb-2 pe-sm-2">
                  <div className="d-flex">
                    <div className="text-muted me-2">Gender:</div>
                    <div>{player.gender}</div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 mb-2">
                  <div className="d-flex">
                    <div className="text-muted me-2">Age Range:</div>
                    <div>{player.ageRange || 'Not specified'}</div>
                  </div>
                </div>

                {/* Row 3: Hand | Backhand */}
                <div className="col-12 col-sm-6 mb-2 pe-sm-2">
                  <div className="d-flex">
                    <div className="text-muted me-2">Hand:</div>
                    <div>{player.dominantHand || 'Not specified'}</div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 mb-2">
                  <div className="d-flex">
                    <div className="text-muted me-2">Backhand:</div>
                    <div>{player.backhand || 'Not specified'}</div>
                  </div>
                </div>

                {/* Row 4: UTR */}
                <div className="col-12 mb-2">
                  <div className="d-flex">
                    <div className="text-muted me-2">UTR:</div>
                    <div><a href={`https://app.utrsports.net/profiles/${player.utrPlayerId}`} target="_blank" rel="noopener noreferrer">View Profile</a></div>
                  </div>
                </div>

                {/* Row 5: Last Updated */}
                <div className="col-12 mb-2">
                  <div className="d-flex">
                    <div className="text-muted me-2">Last Updated:</div>
                    <div>{new Date(player.lastUpdated).toLocaleString()}</div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>
              <h4>Rating History</h4>
            </Card.Header>
            <Card.Body className="p-3">
              {sortedRatings.length > 0 ? (
                <div className="table-responsive">
                  <Table hover size="sm" className="ranking-table" style={{ tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Singles</th>
                        <th>Doubles</th>
                        <th>3-Month</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRatings.map((rating, index) => (
                        <tr key={`${rating.year}-${rating.week}`}>
                          <td>
                            <small>{rating.year}-W{rating.week}</small>
                          </td>
                          <td>
                            <span className="fw-medium">{rating.utr?.singles?.toFixed(2) || '-'}</span>
                          </td>
                          <td>
                            <span className="fw-medium">{rating.utr?.doubles?.toFixed(2) || '-'}</span>
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {rating.utr?.threeMonthRating ? (
                              <>
                                <span className="fw-medium">{rating.utr.threeMonthRating.toFixed(2)}</span>
                                {rating.utr.threeMonthRatingChangeDirection && (
                                  <Badge
                                    bg={rating.utr.threeMonthRatingChangeDirection === 'up' ? 'success' : 'danger'}
                                    className="ms-1 py-1 px-1"
                                    style={{ fontSize: '0.7rem', verticalAlign: 'middle', display: 'inline-block' }}
                                  >
                                    {rating.utr.threeMonthRatingChangeDirection === 'up' ? '↑' : '↓'}
                                    {rating.utr.threeMonthRatingDifference?.toFixed(2)}
                                  </Badge>
                                )}
                              </>
                            ) : <span>-</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p>No rating data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PlayerDetails;
